<template>
	<div class="stock-main-data">
		<div v-if="showMainData && mainData.length" class="main-data-wrapper">
			<div v-for="(item, index) in mainData" :key="index" class="main-data-row">
				<div
					v-for="(data, idx) in item"
					:key="idx"
					class="main-data-col"
					:class="[data.unitStyle ? `main-data-col--${data.unitStyle}` : 'main-data-col--up']"
				>
					<div class="main-data-col-label">{{ data.label }}</div>
					<div class="b-flex-baseline">
						<div class="main-data-col-value">{{ data.value }}</div>
						<div v-if="data.unit" class="main-data-col-unit">{{ data.unit }}</div>
						<RiseFallRange v-if="data.unitStyle" size="8" :type="data.unitStyle" class="ml-4" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import RiseFallRange from '@/components/RiseFallRange.vue';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import { convertNumberToCNUnit } from '@/utils/api';
import { EN } from '@/locales';

// Props
const props = defineProps({
	stockQuoteData: {
		type: Object,
		default: () => ({})
	},
	isPeTtm: {
		type: Boolean,
		default: false
	},
	isEyebright: {
		type: Boolean,
		default: false
	}
});

const { proxy } = getCurrentInstance();
const { isHKStock, isUSStock } = useStockVO();

const showMainData = computed(() => {
	return true;
});

const mainData = ref([]);

const initMainData = result => {
	// 创建数据的深拷贝，避免修改原始数据
	const dataCopy = JSON.parse(JSON.stringify(result));
	let rowArray = [
		[
			{
				label: proxy.$t(`enterprise.chg`),
				prop: 'change'
			},
			{
				label: proxy.$t(`enterprise.%Chg`),
				prop: 'changePct'
			},
			{
				label: proxy.$t(`enterprise.high`),
				prop: 'highPrice'
			},
			{
				label: proxy.$t(`enterprise.low`),
				prop: 'lowPrice'
			}
		],
		[
			{
				label: proxy.$t(`enterprise.turnover`),
				prop: 'turnoverValue'
			},
			{
				label: props.isPeTtm ? proxy.$t(`enterprise.clear-P/E`) : proxy.$t(`enterprise.P/E`),
				prop: 'peTtm'
			},
			{
				label: proxy.$t(`enterprise.market-cap`),
				prop: 'totalMv'
			}
		],
		[
			{
				label: proxy.$t(`enterprise.volume`),
				prop: isHKStock.value ? 'turnoverVolumeShares' : 'turnoverVolume'
			},
			{
				label: proxy.$t(`enterprise.P/B`),
				prop: 'pb'
			},
			{
				label: proxy.$t(`enterprise.float-cap`),
				prop: 'negotiableMv'
			}
		]
	];

	if (isUSStock.value) {
		delete rowArray[2];
		rowArray[1][0] = {
			label: proxy.$t(`enterprise.volume`),
			prop: 'turnoverVolumeShares'
		};
	}

	if (props.isEyebright) {
		let tmpl = [...rowArray];
		tmpl.forEach(parent => {
			while (parent.length % 4 !== 0) {
				// 补空位 凑四个item
				parent.push({ empty: true });
			}
		});
		rowArray = tmpl;
	}

	const formatValueFn = (propName, value) => {
		if (propName == 'changePct') {
			return `${value}%`;
		}
		return value;
	};
	const formatUnitFn = (propName, unit) => {
		if (propName === 'turnoverVolume') {
			return proxy.$i18n.locale === EN ? `${unit}` : `${unit}手`;
		}
		if (propName == 'turnoverVolumeShares') {
			return proxy.$i18n.locale === EN ? `${unit}` : `${unit}股`;
		}
		return unit;
	};
	const formatUnitStyleFn = (propName, value) => {
		if (propName == 'change' || propName == 'changePct') {
			return value > 0 ? 'up' : 'down';
		}
		return null;
	};

	mainData.value = rowArray.map(item => {
		return item.map(childItem => {
			// 中文时可能会使用万手，切换至英文时需要将手置换为股 - A股 一手固定100股
			let displayValue = dataCopy[childItem.prop];
			if (proxy.$i18n.locale === EN && childItem.prop === 'turnoverVolume') {
				displayValue *= 100; // 将 turnoverVolume  单位由手转换为股
			}

			const { value, unit } = convertNumberToCNUnit(displayValue);

			return {
				label: childItem.empty ? '' : childItem.label,
				value: childItem.empty ? '' : formatValueFn(childItem.prop, value),
				unit: formatUnitFn(childItem.prop, unit),
				unitStyle: formatUnitStyleFn(childItem.prop, value)
			};
		});
	});
};

// 监听股票数据变化
watch(
	() => props.stockQuoteData,
	newData => {
		if (newData && Object.keys(newData).length) {
			initMainData(newData);
		}
	},
	{ immediate: true }
);

// 监听语言类型
watch(
	() => proxy.$i18n.locale,
	() => {
		if (props.stockQuoteData && Object.keys(props.stockQuoteData).length) {
			// 使用 nextTick 确保在下一个 tick 中执行，避免无限循环
			proxy.$nextTick(() => {
				initMainData(props.stockQuoteData);
			});
		}
	}
);

// 暴露方法给父组件
defineExpose({
	initMainData
});
</script>

<style lang="less" scoped>
.stock-main-data {
	.main-data-row {
		display: flex;

		& + .main-data-row {
			margin-top: 56px;
		}
	}
	.main-data-col {
		flex: none;
		width: 121px;
		position: relative;

		&:not(:last-child) {
			&::after {
				content: '';
				position: absolute;
				top: 6px;
				right: 0;
				bottom: 6px;
				width: 1px;
				background: #c9cdd4;
			}
		}
		& + .main-data-col {
			margin-left: 37px;
		}
		&--up {
			--unit-style-color: #fa4646;
		}
		&--down {
			--unit-style-color: #30bf54;
		}
		&-label {
			color: #4e5969;

			font-size: 16px;

			font-weight: 400;
			line-height: 140%; /* 22.4px */
		}
		&-value {
			margin-top: 8px;
			color: #333;
			font-family: Barlow;
			font-size: 24px;

			font-weight: 500;
			line-height: normal;
			white-space: nowrap;
		}
		&-unit {
			margin-left: 2px;
			color: #333;
			font-size: 14px;
			font-weight: 400;
			line-height: 20px;
		}
	}

	.main-data-wrapper {
		margin-top: 48px;
	}
}

.eyebright-wrapper {
	.main-data-col {
		flex: 1;
		margin-left: 0 !important;

		&:not(:last-child) {
			&::after {
				display: none;
			}
		}
	}
	.main-data-wrapper {
		margin-top: 24px;
	}
	.kchart-wrapper {
		margin-top: 24px;
		margin-left: -30px;
		margin-right: -12px;
	}
	.stock-number-unit {
		margin-left: 8px;
	}
}
</style>
